import type { TFunction } from 'i18next';
import type { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { cx } from '@/modules/common/helpers/classNames';
import { type ILevelItem, LevelState } from '../../helpers';
import { ScProgressBar } from './styled';

interface IProgressBarProps {
  level: ILevelItem;
  levelNumber: number;
  namespace: string;
  variant?: 'pill';
}

const REWARD_CAPTION_KEY: Partial<Record<LevelState, string>> = {
  [LevelState.Issued]: 'missionLevels.claimLevel',
  [LevelState.Done]: 'missionLevels.card.done',
  [LevelState.Canceled]: 'missionLevels.card.canceled',
};

const getRewardCaption = (t: TFunction, level: ILevelItem) => {
  const rewardCaptionKey = REWARD_CAPTION_KEY[level.state];
  if (rewardCaptionKey) return t(rewardCaptionKey);
  if (level.percent !== 0) {
    return t('missionLevels.toReward', { percent: 100 - level.percent });
  }
  return t('missionLevels.startLevel');
};

export const ProgressBar: FC<IProgressBarProps> = ({
  level,
  levelNumber,
  namespace,
  variant,
}) => {
  const { t } = useTranslation(namespace);
  const issued = level.state === LevelState.Issued;
  const rewardCaption = getRewardCaption(t, level);
  const prize = level.rewardLabel;

  return (
    <ScProgressBar $issued={issued} $percent={level.percent} $variant={variant}>
      <div className="divider" />

      <div className="progressMeter">
        <div className={cx('progressTrack', level.percent === 100 && 'full')}>
          <span className="levelBadge">{levelNumber} lvl</span>

          <div
            className="progressFill"
            style={{ width: `${Math.max(level.percent, 3)}%` }}
          />
        </div>

        <div className="statsRow">
          <div className="statBlock">
            <span className="statValue">{level.progress}</span>
            <span className="statCaption">
              {t('missionLevels.progressCaption')}
            </span>
          </div>

          <div className="statBlock right">
            <span className="statValue">
              <Trans
                components={{
                  span: <span />,
                }}
                i18nKey="missionLevels.prizeLabel"
                ns={namespace}
                values={{
                  prize,
                }}
              />
            </span>
            <span className="statCaption">{rewardCaption}</span>
          </div>
        </div>
      </div>
    </ScProgressBar>
  );
};
